import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import CountryFlag from '../FlagComponent/CountryFlag';
import ChangeAdminForm from './ChangeAdminForm';
import AddNicknameForm from './AddNicknameForm';
import FilePreviewForm from './FilePreviewForm';
import SubmissionForm from './SubmissionForm';
import { FaCheck } from "react-icons/fa6";
import { BiCheckDouble } from "react-icons/bi";
import { MdOutlineCancel, MdOutlineEmojiEmotions } from "react-icons/md";
import { renderMessageContent } from './MessageUtils';
import '../../Styles/ChatWindow.css';
import '../../Styles/Buttons.css';
import AudioRecorder from './AudioRecorder';
import { IoSend } from "react-icons/io5";
import { API_URL } from '../../config';
import EmojiPicker from 'emoji-picker-react';
import { io } from "socket.io-client";
import ClipLoader from 'react-spinners/ClipLoader';
import { toast } from 'react-toastify';

const MESSAGE_PAGE_SIZE = 30;

const compareMessages = (a, b) =>
  (Number(a.timestamp) - Number(b.timestamp)) || (Number(a.id) - Number(b.id));

// Merges fetched pages into what is already on screen, so loading older
// messages or refreshing the newest ones never drops messages already loaded.
const mergeMessages = (current, incoming) => {
  const byId = new Map(current.map((message) => [message.id, message]));
  incoming.forEach((message) => byId.set(message.id, message));
  return [...byId.values()].sort(compareMessages);
};

const ChatWindow = ({isActive}) => {
  const [messages, setMessages] = useState([]);
  const { phone } = useParams();
  const [inputMessage, setInputMessage] = useState('');
  const [isChatSelected, setIsChatSelected] = useState(false);
  const [isActiveState, setIsActiveState] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [pageLoad, setPageload] = useState(false);
  const [pageLoadScreen, setPageloadScreen] = useState(false);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const currentPhoneRef = useRef(phone);
  const isInitialLoadRef = useRef(true);
  const loadingOlderRef = useRef(false);
  const scrollOffsetFromBottomRef = useRef(null);
  const restoredScrollRef = useRef(false);

  const handleMessageChange = (e) => {
    setInputMessage(e.target.value);
  };

  const onEmojiClick = (emojiObject) => {
    handleMessageChange({
      target: {
        value: inputMessage + emojiObject.emoji,
      },
    });
    setIsEmojiPickerOpen(false);
  };

  const fetchMessages = useCallback(async (controller) => {
    if (!phone) {
      setIsActiveState(false);
      setIsChatSelected(false);
      setPageloadScreen(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}getWhatsappMessages/${phone}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          amount: MESSAGE_PAGE_SIZE,
        },
        signal: controller?.signal,
      });
      const data = response.data;
      if (currentPhoneRef.current !== phone) return;
      if (data.status === 'success' && Array.isArray(data.data)) {
        setMessages(prevMessages => mergeMessages(prevMessages, data.data));
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
          setHasOlderMessages(Boolean(data.hasMore));
        }
        setIsChatSelected(true);
        setIsActiveState(true);
        setPageloadScreen(false);
      } else {
        console.warn('Unexpected data format:', data);
      }
    } catch (error) {
    }
  }, [phone]);

  const loadOlderMessages = useCallback(async () => {
    const oldestMessage = messages[0];
    if (!phone || !oldestMessage || !hasOlderMessages || loadingOlderRef.current) {
      return;
    }

    loadingOlderRef.current = true;
    setPageload(true);
    try {
      const response = await axios.get(`${API_URL}getWhatsappMessages/${phone}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          amount: MESSAGE_PAGE_SIZE,
          beforeTimestamp: oldestMessage.timestamp,
          beforeId: oldestMessage.id,
        },
      });
      const data = response.data;
      if (currentPhoneRef.current !== phone) return;
      if (data.status === 'success' && Array.isArray(data.data)) {
        const container = messagesContainerRef.current;
        if (container) {
          scrollOffsetFromBottomRef.current = container.scrollHeight - container.scrollTop;
        }
        setMessages(prevMessages => mergeMessages(prevMessages, data.data));
        setHasOlderMessages(Boolean(data.hasMore));
      }
    } catch (error) {
      console.error('Error loading older messages:', error);
    } finally {
      loadingOlderRef.current = false;
      setPageload(false);
    }
  }, [phone, messages, hasOlderMessages]);

  const handleScroll = (e) => {
    const { scrollTop } = e.target;
    if(scrollTop === 0 && !pageLoadScreen) {
      loadOlderMessages();
    }
  };


  useEffect(() => {
    if (!phone) return;
    const controller = new AbortController();
    fetchMessages(controller);
    const socket = io(process.env.REACT_APP_SOCKETIO, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      timeout: 10000,
    });
    const currentPhone = phone;
    
    const handleNewMessage = (data) => {
      if (data && data.type && data.data) {
        const messageData = data.data;
        if (String(messageData.phone) === String(currentPhone)) {
          setMessages(prevMessages => {
            const existingMessageIndex = prevMessages.findIndex(msg => msg.message_id === messageData.message_id);
            
            if (existingMessageIndex !== -1) {
              const updatedMessages = [...prevMessages];
              updatedMessages[existingMessageIndex] = {
                ...updatedMessages[existingMessageIndex],
                status: messageData.status,
                is_new: messageData.is_new
              };
              return updatedMessages;
            } else {
              return [...prevMessages, messageData];
            }
          });
        }
      } else {
        fetchMessages(controller);
      }
    };
    
    socket.on("newMessage", handleNewMessage);
    socket.on("connect", () => fetchMessages(controller));
    socket.io.on("reconnect", () => fetchMessages(controller));
    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
    });
    
    return () => {
      controller.abort();
      socket.off("newMessage", handleNewMessage);
      socket.disconnect();
    };
  }, [phone, fetchMessages]);

  useEffect(() => {
    currentPhoneRef.current = phone;
    isInitialLoadRef.current = true;
    loadingOlderRef.current = false;
    setMessages([]);
    setHasOlderMessages(false);
    setPageloadScreen(true);
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [phone]);

  const sender = useMemo(() => messages.find(message => message.sent === 0), [messages]);

  const sendMessage = async () => {
    if (inputMessage.trim() !== '' && !isSending) {
      try {
        setIsSending(true);
        await axios.post(`${API_URL}sendWhatsapp`, {
          to: phone,
          message: inputMessage
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setInputMessage('');
        await fetchMessages();
      } catch (error) {
        console.error('Error sending message:', error);
        toast.error(
          error.response?.data?.msg ||
          error.response?.data?.error ||
          'تعذر إرسال الرسالة'
        );
      } finally {
        setIsSending(false);
      }
    }
  };

  // After older messages are added above, keep the same message in view
  // instead of jumping.
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || scrollOffsetFromBottomRef.current === null) return;

    container.scrollTop = container.scrollHeight - scrollOffsetFromBottomRef.current;
    scrollOffsetFromBottomRef.current = null;
    restoredScrollRef.current = true;
  }, [messages]);

  useEffect(() => {
    if (!messagesContainerRef.current) return;
    if (restoredScrollRef.current) {
      restoredScrollRef.current = false;
      setPageload(false);
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const bottomScroll = scrollHeight - clientHeight;
    if(scrollTop > (bottomScroll - 800)) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    setPageload(false);
  }, [messages]);

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    if (now.toDateString() === date.toDateString()) {
      return `${hours}:${minutes}`;
    } else {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      return `${hours}:${minutes} ,${day}/${month}`;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isSending) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      <div className={`${!isActiveState ? 'hide-mobile' : ''} chat-window-container`}>
        {!isChatSelected && (
        <div className="no-chat-selected hide-mobile">
          <p>No chat selected</p>
        </div>
      )}
      {isChatSelected && pageLoadScreen && (
        <div className="no-chat-selected hide-mobile">
          <div className='loading-container'>
              <ClipLoader />
          </div>
        </div>
      )}
      <div className={`chat-main-container ${isChatSelected && !pageLoadScreen ? '' : 'blur'}`}>
        <div className='chat-title-container'>
        {sender && (
          <>
              <div className="flag"><CountryFlag phoneNumber={`+${phone}`} /></div>
              <div className="chat-user-details">
              <div className="chat-title">{sender.nickname || sender.name}</div>
              <div className="phone-number">{sender.phone + '+'}</div>
          </div>
          </>
            )}
          <div className='form-container'>
            <ChangeAdminForm phone={phone} />
            <AddNicknameForm phone={phone} />
          </div>
        </div>
        <div className="chat-messages-container">
          <div className="chat-messages" ref={messagesContainerRef} onScroll={handleScroll}>
            {pageLoad && (
              <div className='loading-container'>
                <ClipLoader />
              </div>
            )}
            {messages.map((message, index) => message.message ? (
              <div key={message.id ?? index} className={`message ${message.sent === 1 ? 'sent' : 'received'}`}>

                <div className="message-content" >
                  <p>
                  {renderMessageContent(message.message , message.sent)}
                  </p>
                </div>
                <div className="message-status-timestamp">
                  <div className="message-status">
                    {message.sent
                      ? message.status === 'sent'
                        ? <FaCheck className='sent-color' />
                        : message.status === 'delivered'
                          ? <BiCheckDouble className='delivered-color' />
                          : message.status === 'read'
                            ? <BiCheckDouble className='read-color' />
                            : message.status === 'failed'
                              ? <MdOutlineCancel className='failed-color' />
                              : ''
                      : ''}
                  </div>
                  <div className="timestamp">{formatDate(message.timestamp)}</div>
                </div>
              </div>
            ) : null)}
            <div id='messageRef' ref={messagesEndRef} />
          </div>
        </div>

        <div className="chat-input-container">
            <SubmissionForm
              onSent={() => fetchMessages()}
            />
          <FilePreviewForm />
          <textarea
            type="text"
            placeholder="اكتب الرسالة هنا"
            onChange={handleMessageChange}
            value={inputMessage}
            onKeyDown={handleKeyDown}
          />
          <div className="emoji-picker-container">
          <MdOutlineEmojiEmotions className="icon icon-emoji" onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}/>
            {isEmojiPickerOpen && (
              <EmojiPicker onEmojiClick={onEmojiClick} />
            )}
          </div>
          <AudioRecorder phone={phone} onRecordingChange={setIsRecording} />
          {!isRecording && (
            <div className="send-icon-container">
              <IoSend 
                className={`icon icon-send-message`} 
                onClick={sendMessage}
                style={{
                  opacity: isSending ? 0.5 : 1,
                  cursor: isSending ? 'not-allowed' : 'pointer',
                  pointerEvents: isSending ? 'none' : 'auto'
                }}
              />
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  );
}

export default ChatWindow;
